import { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { DidService } from '../services/didService';
import { CONNECTION_STATES, PRESET_AVATARS, DEFAULT_CONFIG } from '../constants';

export function useVideoAgent({ idleVideoRef, talkVideoRef }) {
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATES.DISCONNECTED);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(DEFAULT_CONFIG.defaultVoice);
  const [isTalking, setIsTalking] = useState(false);

  const didServiceRef = useRef(null);
  const statsIntervalRef = useRef(null);
  const lastBytesReceivedRef = useRef(0);
  const videoIsPlayingRef = useRef(false);
  const hasSpokenRef = useRef(false);

  // Initialize service instance once
  if (!didServiceRef.current) {
    didServiceRef.current = new DidService();
  }

  const cleanupStreams = useCallback(() => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
    lastBytesReceivedRef.current = 0;
    videoIsPlayingRef.current = false;
    hasSpokenRef.current = false;
    setIsTalking(false);

    if (talkVideoRef.current) {
      talkVideoRef.current.srcObject = null;
      talkVideoRef.current.style.display = 'none';
    }

    if (idleVideoRef.current) {
      idleVideoRef.current.style.display = 'none';
      idleVideoRef.current.src = '';
    }

    if (didServiceRef.current) {
      didServiceRef.current.cleanup();
    }
  }, [idleVideoRef, talkVideoRef]);

  const disconnect = useCallback(async () => {
    try {
      if (didServiceRef.current) {
        await didServiceRef.current.deleteStream();
      }
    } catch (err) {
      console.warn('[VideoAgent] Disconnect warning:', err);
    } finally {
      cleanupStreams();
      setConnectionStatus(CONNECTION_STATES.DISCONNECTED);
      toast('Session disconnected.');
    }
  }, [cleanupStreams]);

  const connect = useCallback(async (customAvatarUrl) => {
    const targetAvatar = customAvatarUrl || avatarUrl;
    if (!targetAvatar) {
      toast.error('Please choose or upload an avatar first.');
      return;
    }

    if (connectionStatus === CONNECTION_STATES.CONNECTED || connectionStatus === CONNECTION_STATES.TALKING) {
      return;
    }

    setConnectionStatus(CONNECTION_STATES.CONNECTING);
    cleanupStreams();

    try {
      const streamData = await didServiceRef.current.createStream(targetAvatar);
      const { offer, ice_servers } = streamData;

      const handleTrack = (event) => {
        // Attach incoming WebRTC media stream immediately to the video element
        if (talkVideoRef.current && event.streams && event.streams[0]) {
          talkVideoRef.current.srcObject = event.streams[0];
          // Prime play() safely
          if (talkVideoRef.current.paused) {
            talkVideoRef.current.play().catch(() => {});
          }
        }

        if (event.track.kind === 'video') {
          if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);

          statsIntervalRef.current = setInterval(async () => {
            const pc = didServiceRef.current?.peerConnection;
            if (!pc) return;

            try {
              const stats = await pc.getStats(event.track);
              stats.forEach((report) => {
                if (report.type === 'inbound-rtp' && report.kind === 'video') {
                  const isPlaying = report.bytesReceived > lastBytesReceivedRef.current;
                  if (isPlaying !== videoIsPlayingRef.current) {
                    videoIsPlayingRef.current = isPlaying;
                    setIsTalking(isPlaying);

                    if (isPlaying) {
                      setConnectionStatus(CONNECTION_STATES.TALKING);
                      if (talkVideoRef.current && hasSpokenRef.current) {
                        talkVideoRef.current.style.display = 'block';
                        if (talkVideoRef.current.paused) {
                          talkVideoRef.current.play().catch(() => {});
                        }
                      }
                    } else {
                      setConnectionStatus(CONNECTION_STATES.CONNECTED);
                      if (talkVideoRef.current) {
                        // Keep stream connected, simply hide layer so idle stage shows
                        // DO NOT CALL pause() on WebRTC live stream!
                        talkVideoRef.current.style.display = 'none';
                      }
                    }
                  }
                  lastBytesReceivedRef.current = report.bytesReceived;
                }
              });
            } catch (statsErr) {
              console.warn('[VideoAgent] Stats warning:', statsErr);
            }
          }, 300);
        }
      };

      const handleIceState = (state) => {
        if (state === 'failed' || state === 'disconnected') {
          console.warn('[VideoAgent] ICE connection state changed:', state);
        }
      };

      const answer = await didServiceRef.current.createPeerConnection(
        offer,
        ice_servers,
        handleTrack,
        handleIceState
      );

      await didServiceRef.current.sendSDPAnswer(answer);

      setConnectionStatus(CONNECTION_STATES.CONNECTED);
      toast.success('AI Avatar connected and ready!');
    } catch (err) {
      console.error('[VideoAgent] Connect error:', err);
      cleanupStreams();
      setConnectionStatus(CONNECTION_STATES.ERROR);
      toast.error(err.message || 'Connection failed. Try selecting another avatar image.');
    }
  }, [avatarUrl, cleanupStreams, connectionStatus, talkVideoRef]);

  const speak = useCallback(async (text) => {
    if (!text || !text.trim()) return;

    const hasActiveStream = Boolean(didServiceRef.current?.isConnected());

    // If WebRTC is active, send talk request to D-ID stream
    if (hasActiveStream) {
      try {
        hasSpokenRef.current = true;
        await didServiceRef.current.sendTalkRequest(text, selectedVoice);
        return;
      } catch (err) {
        console.warn('[VideoAgent] D-ID talk request warning, using speech fallback:', err?.message || err);
      }
    }

    // Audio fallback via Browser Web Speech Synthesis
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.onstart = () => setIsTalking(true);
      utterance.onend = () => setIsTalking(false);
      utterance.onerror = () => setIsTalking(false);
      window.speechSynthesis.speak(utterance);
    }
  }, [connectionStatus, selectedVoice]);

  // Play special keyword video if triggered (e.g. infra showcase)
  const playKeywordVideo = useCallback((videoUrl) => {
    const idleVideo = idleVideoRef.current;
    if (!idleVideo) return;

    idleVideo.style.display = 'block';
    idleVideo.removeAttribute('loop');
    idleVideo.src = videoUrl;
    idleVideo.play().catch(console.error);

    idleVideo.onended = () => {
      idleVideo.style.display = 'none';
      idleVideo.src = '';
      idleVideo.onended = null;
    };
  }, [idleVideoRef]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupStreams();
    };
  }, [cleanupStreams]);

  return {
    connectionStatus,
    isConnected: connectionStatus === CONNECTION_STATES.CONNECTED || connectionStatus === CONNECTION_STATES.TALKING,
    isConnecting: connectionStatus === CONNECTION_STATES.CONNECTING,
    isTalking,
    avatarUrl,
    setAvatarUrl,
    avatarPreview,
    setAvatarPreview,
    selectedVoice,
    setSelectedVoice,
    connect,
    disconnect,
    speak,
    playKeywordVideo,
  };
}
